import { setConfig } from '../actions/configActions';
import { getUserDetails } from '../actions/userActions';
import configureStore from '../store/configureStore';
import RedirectIfAuth from './RedirectIfAuth';
import RequireAuth from './RequireAuth';
import Loading from './common/Loading';
import Banner from './includes/Banner';
import DcnzForm from './includes/DcnzForm';
import Navigation from './includes/Navigation';
import PractitionerPortalPageView from './includes/PractitionerPortalPageView';
import SessionCheck from './includes/SessionCheck';
import ValidateUser from './includes/ValidateUser';
import ForgotPasswordForm from './includes/auth/ForgotPasswordForm';
import LoginCodeVerificationForm from './includes/auth/LoginCodeVerificationForm';
import LoginForm from './includes/auth/LoginForm';
import LogoutRedirect from './includes/auth/LogoutRedirect';
import RegisterForm from './includes/auth/RegisterForm';
import SetPasswordModal from './includes/auth/SetPasswordModal';
import SetPasswordRedirect from './includes/auth/SetPasswordRedirect';
import CreateForm from './layout/CreateForm';
import Dashboard from './layout/Dashboard';
import LearningCourse from './layout/LearningCourse';
import LearningCourses from './layout/LearningCourses';
import NotFound from './layout/NotFound';
import OdooForm from './layout/OdooForm';
import SearchPeer from './layout/SearchPeer';
import SearchVolunteerPeer from './layout/SearchVolunteerPeer';
import SubDashboard from './layout/SubDashboard';
import FormType from './layout/formLists/FormType';
import ManagePeerConfirmationForm from './layout/formLists/ManagePeerConfirmationForm';
import ManagePeerRelationshipForm from './layout/formLists/ManagePeerRelationshipForm';
import ManageRelationshipForm from './layout/formLists/ManageRelationshipForm';
import PeerHistoryForm from './layout/formLists/PeerHistoryForm';
import { useEffect, useState } from 'react';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router';
import { persistStore } from 'redux-persist';

function RegisterRoute() {
  const form = new URLSearchParams(useLocation().search).get('form');
  if (form === 'REG005' || form === 'REG007') {
    return (
      <RequireAuth>
        <CreateForm token={form} />
      </RequireAuth>
    );
  }
  return (
    <RedirectIfAuth>
      <RegisterForm />
    </RedirectIfAuth>
  );
}

function AppShell({ timeout }) {
  const sessionCheckPending = useSelector((state) => state.user.sessionCheckPending);
  const { pathname } = useLocation();
  const contentClassName = pathname === '/learning' ? undefined : 'mb-5';

  if (sessionCheckPending) {
    return <Loading />;
  }

  // prettier-ignore
  return (
    <div className="app">
      <PractitionerPortalPageView />
      <SessionCheck timeout={timeout} />
      <Navigation />
      <Banner />
      <SetPasswordModal />

      <div className={contentClassName}>
      <Routes>
        {/* Logged in only */}
        <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
        <Route path="/learning" element={<RequireAuth><LearningCourses /></RequireAuth>} />
        <Route path="/learning/:courseCode" element={<RequireAuth><LearningCourse /></RequireAuth>} />
        <Route path="/logout" element={<RequireAuth><LogoutRedirect /></RequireAuth>} />
        <Route path="/form/:formType/:formId/:formPage?" element={
          <RequireAuth>
            <OdooForm
              FormComponent={DcnzForm}
              LoadingComponent={Loading}
              NotFoundComponent={NotFound}
            />
          </RequireAuth>
        } />
        {/* Special forms that are very different from the "normal" forms */}
        <Route path="/forms/manage_relationship" element={<RequireAuth><ManageRelationshipForm /></RequireAuth>} />
        <Route path="/forms/manage_other_relationships" element={<RequireAuth><ManagePeerRelationshipForm /></RequireAuth>} />
        <Route path="/forms/view_confirmation_forms" element={<RequireAuth><ManagePeerConfirmationForm /></RequireAuth>} />
        <Route path="/forms/view_annual_declarations" element={<RequireAuth><PeerHistoryForm /></RequireAuth>} />

        {/* "Normal" forms */}
        <Route path="/forms/:type" element={<RequireAuth><FormType /></RequireAuth>} />
        <Route path="/sub/*" element={<RequireAuth><SubDashboard /></RequireAuth>} />
        <Route path="/start/:token?" element={<RequireAuth><CreateForm /></RequireAuth>} />
        <Route path="/set-password" element={<RequireAuth><SetPasswordRedirect /></RequireAuth>} />
        <Route path="/search_peer" element={<RequireAuth><SearchPeer /></RequireAuth>} />
        <Route path="/search_peer_volunteer" element={<RequireAuth><SearchVolunteerPeer /></RequireAuth>} />

        {/* Anonymous only */}
        <Route path="/login" element={<RedirectIfAuth><LoginForm /></RedirectIfAuth>} />
        <Route path="/login-code-verification" element={<RedirectIfAuth><LoginCodeVerificationForm /></RedirectIfAuth>} />
        <Route path="/register/:formId?/:formPage?" element={<RegisterRoute />} />
        <Route path="/reset-password" element={<RedirectIfAuth><ForgotPasswordForm /></RedirectIfAuth>} />

        {/* Any */}
        <Route path="/confirm/:validateToken/:formToken?" element={<ValidateUser />} />

        {/* 404 fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </div>
    </div>
  );
}

const App = ({ config }) => {
  // Configure Redux store once on mount
  const [store] = useState(() => configureStore());

  // Track whether redux-persist has completed rehydration
  const [rehydrated, setRehydrated] = useState(false);

  useEffect(() => {
    // Periodically persist the store into the session
    persistStore(
      store,
      {
        whitelist: ['user', 'dashboard', 'odooField'],
      },
      () => {
        const sessionId = store.getState().user.sessionId;
        if (sessionId) {
          store.dispatch({ type: 'SESSION_CHECK_START' });
          store.dispatch(getUserDetails(sessionId));
        } else {
          store.dispatch({ type: 'SESSION_CHECK_END' });
        }
        // Once we're rehydrated, update local state to re-render the app
        setRehydrated(true);
      },
    );

    // Set application config in redux so we can access it elsewhere
    store.dispatch(setConfig(config));
  }, [store, config]);

  if (!rehydrated) {
    // Show loading spinner while redux-persist rehydrates the store
    return <Loading />;
  }

  // Timeout value (converted to milliseconds)
  const timeout = typeof config.timeout === 'undefined' || config.timeout === 0 ? 10000 : config.timeout * 1000;

  return (
    <Provider store={store}>
      {/* The basename (root URL) of the application is put into the template by SilverStripe */}
      <BrowserRouter basename={config.basename}>
        <AppShell timeout={timeout} />
      </BrowserRouter>
    </Provider>
  );
};

export default App;
